<template>
  <div class="eval-run-list">
    <EvalConfigPanel />

    <div class="panel-header">
      <h3>评测 Run 列表</h3>
      <el-button size="small" :loading="loading" @click="loadRuns">刷新</el-button>
    </div>

    <el-tabs v-model="sourceFilter" class="source-tabs">
      <el-tab-pane :label="`全部 (${runs.length})`" name="all" />
      <el-tab-pane :label="`离线批跑 harness (${harnessCount})`" name="harness" />
      <el-tab-pane :label="`iOS 真实会话 (${iosSessionCount})`" name="ios_session" />
    </el-tabs>

    <el-table
      v-if="filteredRuns.length > 0"
      v-loading="loading"
      :data="filteredRuns"
      stripe
      style="width: 100%"
      class="clickable-table"
      @row-click="handleRowClick"
    >
      <el-table-column label="来源" width="110">
        <template #default="scope">
          <el-tag :type="scope.row.source === 'ios_session' ? 'success' : 'info'" size="small">
            {{ scope.row.source === 'ios_session' ? 'iOS 真实会话' : 'harness' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="执行时间" width="150">
        <template #default="scope">{{ formatTime(scope.row.executed_at) }}</template>
      </el-table-column>
      <el-table-column label="粗推模型" prop="coarse_model" width="130" show-overflow-tooltip />
      <el-table-column label="精排模型" prop="rerank_model" width="130" show-overflow-tooltip />
      <el-table-column label="超量比" width="75">
        <template #default="scope">{{ fmtNum(scope.row.overgen_ratio, 2) }}</template>
      </el-table-column>
      <el-table-column label="匹配器" width="85">
        <template #default="scope">
          <el-tag size="small" type="info">{{ scope.row.matcher }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Query 集" width="120">
        <template #default="scope">
          <span :title="scope.row.query_set_version">{{ (scope.row.query_set_version || '').slice(0, 8) }}</span>
          <span class="muted"> ({{ scope.row.query_set_size }})</span>
        </template>
      </el-table-column>
      <el-table-column label="样本数" prop="sample_count" width="75" />
      <el-table-column label="幻觉率" width="85">
        <template #default="scope">{{ fmtPct(scope.row.metrics_summary?.hallucination_rate?.mean) }}</template>
      </el-table-column>
      <el-table-column label="存活率" width="85">
        <template #default="scope">{{ fmtPct(scope.row.metrics_summary?.survival_vs_requested?.mean) }}</template>
      </el-table-column>
      <el-table-column label="ρ均值" width="85">
        <template #default="scope">{{ fmtNum(scope.row.metrics_summary?.order_spearman_vs_pool?.mean) }}</template>
      </el-table-column>
      <el-table-column label="降级率" width="85">
        <template #default="scope">{{ fmtPct(scope.row.metrics_summary?.degraded_rate) }}</template>
      </el-table-column>
      <el-table-column label="标记" width="170">
        <template #default="scope">
          <el-tag v-if="scope.row.is_baseline" type="warning" size="small" style="margin-right: 4px;">基线</el-tag>
          <el-tag v-if="scope.row.config_source === 'local'" type="danger" size="small">本地未确认</el-tag>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-else-if="!loading" description="暂无评测 run，先用 eval_harness 跑一次并 --ingest 摄取" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';
import EvalConfigPanel from './EvalConfigPanel.vue';

const emit = defineEmits(['select-run']);

const runs = ref([]);
const loading = ref(false);
// 'all' | 'harness' | 'ios_session' —— 纯前端过滤，不加后端查询参数（eval.py 摄取/查询契约零改动）
const sourceFilter = ref('all');

const harnessCount = computed(() => runs.value.filter((r) => r.source !== 'ios_session').length);
const iosSessionCount = computed(() => runs.value.filter((r) => r.source === 'ios_session').length);

const filteredRuns = computed(() => {
  if (sourceFilter.value === 'all') return runs.value;
  if (sourceFilter.value === 'ios_session') return runs.value.filter((r) => r.source === 'ios_session');
  return runs.value.filter((r) => r.source !== 'ios_session');
});

const fmtPct = (v) => (v === null || v === undefined ? '-' : `${(v * 100).toFixed(1)}%`);
const fmtNum = (v, digits = 3) => (v === null || v === undefined ? '-' : Number(v).toFixed(digits));
const formatTime = (v) => (v ? new Date(v).toLocaleString() : '-');

const loadRuns = async () => {
  loading.value = true;
  try {
    const resp = await axios.get('/api/eval/runs');
    if (resp.data.code === 1) {
      runs.value = resp.data.data || [];
    } else {
      ElMessage.error(resp.data.msg || '加载评测 run 列表失败');
    }
  } catch (err) {
    console.error('加载评测 run 列表失败:', err);
    ElMessage.error(err.response?.data?.msg || '加载评测 run 列表失败');
  } finally {
    loading.value = false;
  }
};

const handleRowClick = (row) => {
  emit('select-run', row.run_id);
};

onMounted(() => {
  loadRuns();
});
</script>

<style scoped>
.eval-run-list .panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.clickable-table :deep(.el-table__row) {
  cursor: pointer;
}

.source-tabs {
  margin-bottom: 12px;
}

.muted {
  color: #909399;
}
</style>
